import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

import { getTheme, type ThemeResponse } from "@/api/theme";

const FALLBACK_THEME: ThemeResponse = {
  app_name: "ESL Sync",
  app_subtitle: "Admin portal",
  company_name: "Infogram doo Beograd",
  primary_color: "#0f172a",
  accent_color: "#3b82f6",
  logo_url: null,
  login_message: null,
};

const ThemeContext = createContext<ThemeResponse>(FALLBACK_THEME);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: ["theme"],
    queryFn: getTheme,
    staleTime: Infinity,  // theme se ne menja u toku života page-a
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const theme = useMemo<ThemeResponse>(() => data ?? FALLBACK_THEME, [data]);

  // Tailwind koristi HSL CSS variables (`hsl(var(--primary))`); konvertujemo
  // hex iz theme-a u "H S% L%" format i override-ujemo --primary + --ring
  // (focus state). To znači sve `bg-primary` / `text-primary` / `ring-ring`
  // utility-ji u kodu automatski poprime brending boju.
  //
  // VAŽNO: NE override-ujemo `--accent` jer je u shadcn/ui sistemu to hover
  // background za button-e (treba da ostane muted-pastel). Theme accent boja
  // ide u `--theme-accent` da se može koristiti eksplicitno gde treba.
  useEffect(() => {
    const root = document.documentElement;
    const primaryHsl = hexToHslTuple(theme.primary_color);
    if (primaryHsl) {
      root.style.setProperty("--primary", primaryHsl);
      root.style.setProperty("--ring", primaryHsl);
    }
    const accentHsl = hexToHslTuple(theme.accent_color);
    if (accentHsl) {
      root.style.setProperty("--theme-accent", accentHsl);
    }
    document.title = theme.app_name;
  }, [theme.primary_color, theme.accent_color, theme.app_name]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeResponse {
  return useContext(ThemeContext);
}

/** Konvertuje "#rrggbb" u Tailwind HSL var format `H S% L%`. Vraća null za nevažeći hex. */
function hexToHslTuple(input: string): string | null {
  const match = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(input.trim());
  if (!match || !match[1]) return null;
  let hex: string = match[1];
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
